/**
 * useListViewScroll — puts you back where you were in a list after opening a row.
 *
 * The other half of the state a list page loses on unmount. The query itself
 * belongs in the URL (see `useUrlListState`); scroll offset does not — it is
 * meaningless to a reader, unhelpful in a shared link, and only worth anything
 * for the few minutes around the round trip that produced it. So it lives in
 * sessionStorage: per-tab, and gone when the tab is.
 *
 * TWO THINGS MAKE NAIVE SCROLL RESTORATION SILENTLY DO NOTHING, and both are
 * handled here:
 *
 *   1. Restoring before the rows exist. The page has no height until the fetch
 *      resolves, so an early `scrollTo` clamps to 0 and looks like a no-op.
 *      Restoration therefore waits on a `ready` flag from the caller.
 *   2. Restoring more than once. Re-running on every render would yank the page
 *      back the moment the user scrolls away, so each query is restored once.
 *
 * Offsets are stored against the query they were taken at, one entry per route.
 * Scroll position belongs to a specific set of results: coming back to the same
 * filters restores it, and changing the filters returns you to the top, which is
 * what you want when the rows underneath have changed.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_PREFIX = 'listScroll:';

export interface StoredScroll {
  /** The query string the offset was captured at. */
  key: string;
  y: number;
}

/** sessionStorage is unavailable or throws in private windows and with site data
 *  blocked, and a lost scroll offset is never worth breaking a page over. */
function readStored(routeKey: string): StoredScroll | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + routeKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as StoredScroll).key !== 'string' ||
      typeof (parsed as StoredScroll).y !== 'number'
    ) {
      return null;
    }
    return parsed as StoredScroll;
  } catch {
    return null;
  }
}

function writeStored(routeKey: string, value: StoredScroll): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + routeKey, JSON.stringify(value));
  } catch {
    // Quota or a blocked store — nothing to recover, and nothing worth throwing.
  }
}

/**
 * Where the page should sit for `key`, given what was stored. Returning 0 for a
 * miss is deliberate: after a filter change the rows are different, so the top
 * is the right place to be rather than wherever the previous results left you.
 */
export function resolveScrollTarget(stored: StoredScroll | null, key: string): number {
  if (!stored || stored.key !== key || !Number.isFinite(stored.y) || stored.y <= 0) return 0;
  return stored.y;
}

/**
 * @param ready True once the list has rendered rows — until then the document is
 *              too short for a restore to land. Typically `!loading && rows > 0`.
 */
export function useListViewScroll(ready: boolean): void {
  const { pathname, search } = useLocation();
  const restoredFor = useRef<string | null>(null);

  // Record the offset as it changes, against the query in force at the time.
  // Writing here rather than in a cleanup avoids the ordering trap where a
  // filter change scrolls the page before the old offset has been saved.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      // Coalesced to one write per frame, and only while actually scrolling.
      frame = requestAnimationFrame(() => {
        frame = 0;
        writeStored(pathname, { key: search, y: window.scrollY });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname, search]);

  // Layout effect, so the page is already in place on the frame it paints —
  // restoring in a passive effect shows the top of the list first, then jumps.
  useLayoutEffect(() => {
    if (!ready || restoredFor.current === search) return;
    restoredFor.current = search;
    window.scrollTo(0, resolveScrollTarget(readStored(pathname), search));
  }, [ready, pathname, search]);
}
